<template>
  <div class="subsidy-flow-page">
    <div class="activity-title">活动：{{ activityTitle }}</div>

    <div class="filter-bar">
      <el-input
        class="filter-select-input"
        :value="relDealers.length ? dealerButtonText : ''"
        placeholder="选择经销商"
        readonly
        size="small"
        @click.native="openDealerSelect"
      >
        <i
          slot="suffix"
          class="el-input__icon el-icon-search"
          @click.stop="openDealerSelect"
        />
      </el-input>
      <el-input
        class="filter-select-input"
        :value="relStores.length ? storeButtonText : ''"
        placeholder="选择门店"
        readonly
        size="small"
        @click.native="openStoreSelect"
      >
        <i
          slot="suffix"
          class="el-input__icon el-icon-search"
          @click.stop="openStoreSelect"
        />
      </el-input>
      <el-select
        v-model="searchForm.installmentNum"
        placeholder="分期数"
        clearable
        size="small"
        class="filter-select"
      >
        <el-option
          v-for="item in PeriodOptions"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        />
      </el-select>
      <el-select
        v-model="searchForm.ownerType"
        placeholder="活动归属"
        clearable
        size="small"
        class="filter-select"
      >
        <el-option
          v-for="item in ownerTypeOptions"
          :key="item.value"
          :label="item.label"
          :value="item.value"
        />
      </el-select>
      <el-button type="primary" size="small" @click="handleSearch">搜索</el-button>
      <el-button type="primary" size="small" :loading="exporting" @click="handleExport">导出流水</el-button>
    </div>

    <el-table
      v-loading="loading"
      :data="tableData"
      class="subsidy-flow-table"
    >
      <el-table-column prop="order_id" label="订单号" min-width="180" />
      <el-table-column label="下单门店" min-width="180">
        <template slot-scope="{ row }">
          <div>{{ getStoreName(row) }}</div>
          <div class="cell-sub">Apple ID: {{ getStoreAppleId(row) }}</div>
        </template>
      </el-table-column>
      <el-table-column label="经销商" min-width="200">
        <template slot-scope="{ row }">
          <div>{{ getDealerName(row) }}</div>
          <div class="cell-sub">HQID: {{ getDealerHqid(row) }}</div>
        </template>
      </el-table-column>
      <el-table-column label="订单分期数" width="110" align="center">
        <template slot-scope="{ row }">
          {{ getInstallmentNum(row) }}
        </template>
      </el-table-column>
      <el-table-column label="活动归属" width="100">
        <template slot-scope="{ row }">
          {{ getOwnerTypeLabel(row) }}
        </template>
      </el-table-column>
      <el-table-column label="账单交易金额" width="130">
        <template slot-scope="{ row }">
          ¥{{ formatAmount(row.trade_amount) }}
        </template>
      </el-table-column>
      <el-table-column label="分期服务费" width="120">
        <template slot-scope="{ row }">
          ¥{{ formatAmount(getServiceFee(row)) }}
        </template>
      </el-table-column>
      <el-table-column label="手续费" width="140">
        <template slot-scope="{ row }">
          <template v-if="getPayExtInfo(row)">
            <div>手续费：¥{{ formatAmount(getPayExtInfo(row).fee_amt) }}</div>
            <div class="cell-sub">分期手续费：¥{{ formatAmount(getPayExtInfo(row).fq_fee_amt) }}</div>
          </template>
          <span v-else>-</span>
        </template>
      </el-table-column>
      <el-table-column label="下单时间" min-width="160">
        <template slot-scope="{ row }">
          {{ formatOrderTime(row) }}
        </template>
      </el-table-column>
      <el-table-column label="交易时间" min-width="160">
        <template slot-scope="{ row }">
          {{ row.trade_time || (row.raw_data && row.raw_data.tradeTime) || '-' }}
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination-wrap">
      <el-pagination
        background
        layout="total, sizes, prev, pager, next, jumper"
        :current-page.sync="pagination.page"
        :page-size.sync="pagination.pageSize"
        :page-sizes="[10, 20, 50]"
        :total="pagination.total"
        @size-change="handlePageChange"
        @current-change="handlePageChange"
      />
    </div>

    <DealerSelect
      :store-visible="dealerVisible"
      :is-valid="true"
      :rel-data-ids="relDealers"
      :get-status="dealerSelectStatus"
      @chooseStore="handleDealerSelected"
      @closeStoreDialog="dealerVisible = false"
    />
    <StoreSelect
      :store-visible="storeVisible"
      :is-valid="true"
      :rel-data-ids="relStores"
      :get-status="true"
      :set-page-size="100"
      :query-params="storeSelectQueryParams"
      @chooseStore="handleStoreSelected"
      @closeStoreDialog="storeVisible = false"
    />
  </div>
</template>

<script>
import DealerSelect from '@/components/function/dealerSelect'
import StoreSelect from '@/components/storeListSelect'
import { getActivityFlows } from '../api'
import { OwnerTypeMap, PeriodOptions } from '../config'

export default {
  name: 'InterestFreeActivitySubsidyFlow',
  components: {
    DealerSelect,
    StoreSelect
  },
  data () {
    return {
      OwnerTypeMap,
      PeriodOptions,
      ownerTypeOptions: Object.keys(OwnerTypeMap).map(key => ({
        label: OwnerTypeMap[key],
        value: key
      })),
      loading: false,
      exporting: false,
      tableData: [],
      relDealers: [],
      relStores: [],
      dealerVisible: false,
      storeVisible: false,
      dealerSelectStatus: false,
      dealerButtonText: '选择经销商',
      storeButtonText: '选择门店',
      searchForm: {
        installmentNum: '',
        ownerType: ''
      },
      pagination: {
        page: 1,
        pageSize: 20,
        total: 0
      }
    }
  },
  computed: {
    activityId () {
      return this.$route.query.id || ''
    },
    activityName () {
      return this.$route.query.activity_name || ''
    },
    activityTitle () {
      if (this.activityId && this.activityName) {
        return `${this.activityId}_${this.activityName}`
      }
      if (this.activityId) return String(this.activityId)
      return '全部'
    },
    storeSelectQueryParams () {
      const dealerIds = this.relDealers
        .map(item => Number(item.dealer_id))
        .filter(id => !Number.isNaN(id))
      return dealerIds.length ? { dealerId: dealerIds } : {}
    }
  },
  mounted () {
    this.loadTableData()
  },
  methods: {
    formatAmount (value) {
      if (value == null || value === '') return '-'
      const num = Number(value)
      if (Number.isNaN(num)) return value
      return Number.isInteger(num) ? String(num) : num.toFixed(2)
    },
    formatTimestamp (timestamp) {
      if (timestamp == null || timestamp === '') return ''
      // 已是可读时间字符串则直接返回
      if (typeof timestamp === 'string' && timestamp.includes('-')) return timestamp
      const ts = Number(timestamp)
      if (Number.isNaN(ts)) return String(timestamp)
      const ms = ts > 1e12 ? ts : ts * 1000
      const pad = (n) => String(n).padStart(2, '0')
      const d = new Date(ms)
      return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
    },
    formatOrderTime (row) {
      const orderInfo = row.order_info || {}
      const created = orderInfo.created || orderInfo.create_time || row.created
      return this.formatTimestamp(created) || '-'
    },
    getStoreName (row) {
      const info = row.store_info || {}
      return info.alias_name || info.name || row.store_name || row.distributor_name || '-'
    },
    getStoreAppleId (row) {
      const info = row.store_info || {}
      return info.shop_code || row.shop_code || row.apple_id || '-'
    },
    getDealerName (row) {
      const info = row.dealer_info || {}
      return info.name || info.alias_name || row.dealer_name || (row.dealer_id ? `经销商ID: ${row.dealer_id}` : '-')
    },
    getDealerHqid (row) {
      const info = row.dealer_info || {}
      return info.hqid || row.dealer_hqid || row.hqid || '-'
    },
    getInstallmentNum (row) {
      if (row.installment_num != null && row.installment_num !== '' && Number(row.installment_num) > 0) {
        return row.installment_num
      }
      const tradeInfo = row.trade_info
      if (Array.isArray(tradeInfo) && tradeInfo.length) {
        const num = tradeInfo[0].fund_fq_num
        return num != null && num !== '' ? num : '-'
      }
      if (tradeInfo && typeof tradeInfo === 'object') {
        const num = tradeInfo.fund_fq_num
        return num != null && num !== '' ? num : '-'
      }
      return '-'
    },
    getOwnerTypeLabel (row) {
      const ownerType = row.owner_type || (row.subsidy_snapshot && row.subsidy_snapshot.owner_type)
      if (!ownerType) return '-'
      return this.OwnerTypeMap[ownerType] || ownerType
    },
    getPayExtInfo (row) {
      if (!row || row.pay_ext_info == null || row.pay_ext_info === '') return null
      if (typeof row.pay_ext_info === 'object') return row.pay_ext_info
      try {
        const parsed = JSON.parse(row.pay_ext_info)
        return parsed && typeof parsed === 'object' ? parsed : null
      } catch (e) {
        return null
      }
    },
    // 接口暂无 service_fee，优先用优惠/商贴金额（与 raw_data.discountAmount 一致）
    getServiceFee (row) {
      if (!row) return null
      if (row.service_fee != null && row.service_fee !== '') return row.service_fee
      if (row.installment_service_fee != null && row.installment_service_fee !== '') return row.installment_service_fee
      if (row.discount_amount != null && row.discount_amount !== '') return row.discount_amount
      if (row.subsidy_amount_yuan != null && row.subsidy_amount_yuan !== '') return row.subsidy_amount_yuan
      if (row.raw_data && row.raw_data.discountAmount != null) return row.raw_data.discountAmount
      return null
    },
    buildQueryParams () {
      const dealerIds = this.relDealers
        .map(item => item.dealer_id)
        .filter(id => id != null && id !== '')
      const distributorIds = this.relStores
        .map(item => item.distributor_id)
        .filter(id => id != null && id !== '')
      const params = {
        page: String(this.pagination.page),
        pageSize: String(this.pagination.pageSize)
      }
      if (this.activityId) {
        params.activity_id = this.activityId
      }
      if (dealerIds.length) {
        params.dealer_id = dealerIds.join(',')
      }
      if (distributorIds.length) {
        params.distributor_id = distributorIds.join(',')
      }
      if (this.searchForm.installmentNum !== '' && this.searchForm.installmentNum != null) {
        params.installment_num = this.searchForm.installmentNum
      }
      if (this.searchForm.ownerType) {
        params.owner_type = this.searchForm.ownerType
      }
      return params
    },
    async loadTableData () {
      this.loading = true
      try {
        const res = await getActivityFlows(this.buildQueryParams())
        const data = res.data.data || {}
        this.tableData = data.list || []
        this.pagination.total = data.total_count || 0
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || e.message || '获取活动流水失败'
        this.$message.error(msg)
        this.tableData = []
        this.pagination.total = 0
      } finally {
        this.loading = false
      }
    },
    handleSearch () {
      this.pagination.page = 1
      this.loadTableData()
    },
    handlePageChange () {
      this.loadTableData()
    },
    handleExport () {
      const params = this.buildQueryParams()
      delete params.page
      delete params.pageSize
      params.is_export = 1
      this.exporting = true
      getActivityFlows(params).then(() => {
        this.$message.success('导出任务已提交，请至「设置-导出列表」查看及下载')
      }).catch((e) => {
        const msg = (e.response && e.response.data && e.response.data.message) || e.message || '导出失败'
        this.$message.error(msg)
      }).finally(() => {
        this.exporting = false
      })
    },
    openDealerSelect () {
      this.dealerSelectStatus = true
      this.dealerVisible = true
    },
    handleDealerSelected (list) {
      this.dealerVisible = false
      const previousIds = this.relDealers.map(item => Number(item.dealer_id)).sort().join(',')
      const nextIds = (list || []).map(item => Number(item.dealer_id)).sort().join(',')
      this.relDealers = list || []
      this.dealerButtonText = this.relDealers.length
        ? `已选中${this.relDealers.length}个经销商`
        : '选择经销商'
      if (previousIds !== nextIds) {
        this.relStores = []
        this.storeButtonText = '选择门店'
      }
    },
    openStoreSelect () {
      this.storeVisible = true
    },
    handleStoreSelected (list) {
      this.storeVisible = false
      this.relStores = list || []
      this.storeButtonText = this.relStores.length
        ? `已选中${this.relStores.length}个门店`
        : '选择门店'
    }
  }
}
</script>

<style scoped lang="scss">
.subsidy-flow-page {
  padding: 0 20px 20px;

  .activity-title {
    margin-bottom: 16px;
    font-size: 14px;
    color: #303133;
    line-height: 1.5;
  }

  .filter-bar {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
    margin-bottom: 20px;

    .filter-select-input,
    .filter-select {
      width: 180px;
    }

    .filter-select-input {
      cursor: pointer;

      ::v-deep .el-input__inner {
        cursor: pointer;
      }

      .el-icon-search {
        cursor: pointer;
      }
    }
  }

  .subsidy-flow-table {
    .cell-sub {
      margin-top: 2px;
      font-size: 12px;
      color: #909399;
      line-height: 1.4;
    }
  }

  .pagination-wrap {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
  }
}
</style>
